import { memo, MouseEvent, useMemo, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import ReplyRoundedIcon from "@mui/icons-material/ReplyRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import dayjs from "dayjs";
import { Comentario, ComposerPayload, formatarDataHora } from "../../pages/suporte/types";
import { styless } from "../../pages/suporte/styles";
import { hidratarInline, idsInlineDoHtml } from "../../utils/suporte/anexosInline";
import { sanitizarCorpo } from "../../utils/suporte/sanitize";
import { AnexoThumbs } from "./anexos";
import Composer from "./Composer";
import { UserAvatar } from "./shared";

interface BolhaProps {
  comentario: Comentario;
  usuarioId: string;
  resposta?: boolean;
  readOnly: boolean;
  editandoId: string | null;
  onPreview: (url: string) => void;
  onEditando: (id: string | null) => void;
  onSalvarEdicao: (id: string, payload: ComposerPayload) => Promise<void>;
}

const Bolha = memo(function Bolha({
  comentario,
  usuarioId,
  resposta,
  readOnly,
  editandoId,
  onPreview,
  onEditando,
  onSalvarEdicao,
}: BolhaProps) {
  const theme = useTheme();
  const styles = styless(theme);

  const { html, naFaixa } = useMemo(() => {
    const inline = new Set(idsInlineDoHtml(comentario.texto));
    return {
      html: sanitizarCorpo(hidratarInline(comentario.texto, comentario.anexos)),
      naFaixa: comentario.anexos.filter((anexo) => !inline.has(anexo.id)),
    };
  }, [comentario.texto, comentario.anexos]);

  const proprio = comentario.autorId === usuarioId;
  const editando = editandoId === comentario.id;

  const abrirPreview = (e: MouseEvent<HTMLDivElement>) => {
    const img = (e.target as HTMLElement).closest("img[data-anexo-id]");
    const src = img?.getAttribute("src");
    if (src) onPreview(src);
  };

  if (editando) {
    return (
      <Box sx={{ py: 0.5 }}>
        <Composer
          placeholder="Edite sua mensagem…"
          valorInicial={comentario.texto}
          anexosPublicados={comentario.anexos}
          onPreview={onPreview}
          textoBotao="Salvar"
          autoFoco
          onCancelar={() => onEditando(null)}
          onEnviar={(payload) => onSalvarEdicao(comentario.id, payload)}
        />
      </Box>
    );
  }

  return (
    <Box sx={styles.mensagemLinha(proprio)}>
      <UserAvatar
        nome={comentario.autor}
        foto={comentario.autorFoto}
        tamanho={resposta ? 26 : 32}
      />
      <Box sx={styles.bolhaColuna(proprio)}>
        <Box sx={styles.bolhaMeta(proprio)}>
          <Typography sx={styles.bolhaAutor}>
            {proprio ? "Você" : comentario.autor}
          </Typography>
          <Tooltip title={formatarDataHora(comentario.criadoEm)} arrow>
            <Typography sx={styles.bolhaHora}>
              {dayjs(comentario.criadoEm).format("DD/MM · HH:mm")}
            </Typography>
          </Tooltip>
        </Box>

        <Box sx={styles.bolha(proprio, !!resposta)}>
          {proprio && !readOnly && (
            <Box className="bolha-acoes" sx={styles.bolhaAcoes}>
              <Tooltip title="Editar mensagem" arrow>
                <IconButton
                  size="small"
                  onClick={() => onEditando(comentario.id)}
                >
                  <EditOutlinedIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {html && (
            <Typography
              component="div"
              sx={styles.corpoHtml}
              onClick={abrirPreview}
              dangerouslySetInnerHTML={{ __html: html }}
            />
          )}
          {!!naFaixa.length && (
            <Box sx={{ mt: html ? 1.25 : 0 }}>
              <AnexoThumbs itens={naFaixa} onPreview={onPreview} />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
});

interface GrupoProps {
  comentario: Comentario;
  usuarioId: string;
  readOnly: boolean;
  editandoId: string | null;
  onPreview: (url: string) => void;
  onEditando: (id: string | null) => void;
  onSalvarEdicao: (id: string, payload: ComposerPayload) => Promise<void>;
  onResponder: (id: string, payload: ComposerPayload) => Promise<void>;
}

const Grupo = memo(function Grupo({
  comentario,
  usuarioId,
  readOnly,
  editandoId,
  onPreview,
  onEditando,
  onSalvarEdicao,
  onResponder,
}: GrupoProps) {
  const theme = useTheme();
  const styles = styless(theme);
  const [respondendo, setRespondendo] = useState(false);

  const enviarResposta = async (payload: ComposerPayload) => {
    await onResponder(comentario.id, payload);
    setRespondendo(false);
  };

  return (
    <Box sx={styles.grupoMensagem}>
      <Bolha
        comentario={comentario}
        usuarioId={usuarioId}
        readOnly={readOnly}
        editandoId={editandoId}
        onPreview={onPreview}
        onEditando={onEditando}
        onSalvarEdicao={onSalvarEdicao}
      />

      {!!comentario.respostas.length && (
        <Box sx={styles.respostasTrilho}>
          {comentario.respostas.map((item) => (
            <Bolha
              key={item.id}
              comentario={item}
              usuarioId={usuarioId}
              resposta
              readOnly={readOnly}
              editandoId={editandoId}
              onPreview={onPreview}
              onEditando={onEditando}
              onSalvarEdicao={onSalvarEdicao}
            />
          ))}
        </Box>
      )}

      {!readOnly && !respondendo && (
        <Button
          size="small"
          startIcon={<ReplyRoundedIcon sx={{ fontSize: 16 }} />}
          sx={styles.responderBotao}
          onClick={() => setRespondendo(true)}
        >
          Responder
        </Button>
      )}

      <Collapse in={respondendo} timeout={220} unmountOnExit>
        <Box sx={styles.respostaComposer}>
          <Composer
            autoFoco
            placeholder="Escreva uma resposta…"
            textoBotao="Responder"
            onPreview={onPreview}
            onCancelar={() => setRespondendo(false)}
            onEnviar={enviarResposta}
          />
        </Box>
      </Collapse>
    </Box>
  );
});

interface ComentariosProps {
  comentarios: Comentario[];
  usuarioId: string;
  readOnly: boolean;
  onPreview: (url: string) => void;
  onResponder: (
    comentarioId: string,
    payload: ComposerPayload,
  ) => Promise<void>;
  onEditar: (comentarioId: string, payload: ComposerPayload) => Promise<void>;
}

export default function Comentarios({
  comentarios,
  usuarioId,
  readOnly,
  onPreview,
  onResponder,
  onEditar,
}: ComentariosProps) {
  const theme = useTheme();
  const styles = styless(theme);
  const [editandoId, setEditandoId] = useState<string | null>(null);

  const total = comentarios.reduce(
    (soma, comentario) => soma + 1 + comentario.respostas.length,
    0,
  );

  const salvarEdicao = async (id: string, payload: ComposerPayload) => {
    await onEditar(id, payload);
    setEditandoId(null);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography sx={styles.secaoTitulo}>
        <ForumOutlinedIcon sx={{ fontSize: 17 }} />
        Comentários
        {total > 0 && (
          <Box component="span" sx={styles.contadorSecao}>
            {total}
          </Box>
        )}
      </Typography>

      {comentarios.length === 0 ? (
        <Box sx={styles.conversaVazia}>
          <SendRoundedIcon sx={{ fontSize: 28, opacity: 0.5 }} />
          <Typography sx={{ fontSize: "0.86rem", fontWeight: 600 }}>
            Nenhum comentário ainda
          </Typography>
          <Typography sx={{ fontSize: "0.8rem" }}>
            {readOnly
              ? "Este chamado foi concluído sem comentários."
              : "Escreva abaixo para falar com a equipe de suporte."}
          </Typography>
        </Box>
      ) : (
        <Box sx={styles.conversa}>
          {comentarios.map((comentario) => (
            <Grupo
              key={comentario.id}
              comentario={comentario}
              usuarioId={usuarioId}
              readOnly={readOnly}
              editandoId={editandoId}
              onPreview={onPreview}
              onEditando={setEditandoId}
              onSalvarEdicao={salvarEdicao}
              onResponder={onResponder}
            />
          ))}
        </Box>
      )}

      {readOnly && (
        <Box sx={styles.encerrada}>
          <LockOutlinedIcon sx={{ fontSize: 16 }} />
          Chamado concluído — não é possível enviar novos comentários.
        </Box>
      )}
    </Box>
  );
}
